import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  runtimeDiagnosticsService,
  runtimeHistoryService,
  runtimeRetryService,
} from '../../services/runtimeOperationalService';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import {
  RuntimeCorrelationChip,
  RuntimeEmptyState,
  RuntimeErrorState,
  RuntimeLoadingState,
} from '../common/runtime';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;

const STATUS_COLOR = {
  success: 'green',
  completed: 'green',
  running: 'processing',
  pending: 'gold',
  failed: 'red',
  error: 'red',
  cancelled: 'default',
};

const RISK_COLOR = {
  low: 'green',
  medium: 'gold',
  high: 'red',
};

const RISKY_REASON_CODES = new Set([
  'critical_mismatch',
  'recreate_not_safe',
  'provider_not_mock',
  'fingerprint_drift',
]);

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const statusBadge = (value) => {
  const normalized = String(value || '').toLowerCase();
  const color = STATUS_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const riskTag = (value) => {
  const normalized = String(value || '').toLowerCase();
  const color = RISK_COLOR[normalized] || 'default';
  return <Tag color={color}>{String(value || '-').toUpperCase()}</Tag>;
};

const includeByExecution = (item, executionMatch) => {
  if (!item || !executionMatch) {
    return false;
  }

  const issueId = String(item.issue_id || '').trim();
  const invoiceId = String(item.invoice_id || '').trim();
  const chargeId = String(item.charge_id || '').trim();

  return (
    (issueId && executionMatch.issueIds.has(issueId)) ||
    (invoiceId && executionMatch.invoiceIds.has(invoiceId)) ||
    (chargeId && executionMatch.chargeIds.has(chargeId))
  );
};

const buildExecutionMatch = (execution) => {
  const items = Array.isArray(execution?.items) ? execution.items : [];

  const issueIds = new Set(
    items.map((item) => String(item?.issue_id || '').trim()).filter(Boolean)
  );
  const invoiceIds = new Set(
    items.map((item) => String(item?.invoice_id || '').trim()).filter(Boolean)
  );
  const chargeIds = new Set(
    items.map((item) => String(item?.charge_id || '').trim()).filter(Boolean)
  );

  return { issueIds, invoiceIds, chargeIds };
};

const hasMatchSignals = (executionMatch) => {
  if (!executionMatch) {
    return false;
  }
  return (
    executionMatch.issueIds.size > 0 ||
    executionMatch.invoiceIds.size > 0 ||
    executionMatch.chargeIds.size > 0
  );
};

const computeEstimatedRisk = (manualItems, planItems) => {
  const hasHighFromManual = manualItems.some(
    (item) => String(item?.risk_level || '').toLowerCase() === 'high'
  );

  const hasHighFromPlan = planItems.some((item) =>
    Array.isArray(item?.reason_codes)
      ? item.reason_codes.some((code) => RISKY_REASON_CODES.has(String(code || '').toLowerCase()))
      : false
  );

  if (hasHighFromManual || hasHighFromPlan) {
    return 'high';
  }

  const hasMedium = manualItems.some(
    (item) => String(item?.risk_level || '').toLowerCase() === 'medium'
  );
  if (hasMedium || planItems.length > 0) {
    return 'medium';
  }

  return 'low';
};

const PlatformRuntimeRetryPreview = () => {
  const navigate = useNavigate();
  const { executionId } = useParams();
  const normalizedExecutionId = String(executionId || '').trim();

  const executionQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.executionDetail(normalizedExecutionId),
    enabled: Boolean(normalizedExecutionId),
    queryFn: async () => {
      const response = await runtimeHistoryService.getExecutionDetail(normalizedExecutionId);
      return response.data;
    },
  });

  const execution = executionQuery.data || null;
  const tenantId = String(execution?.tenant_id || '').trim();

  const previewParams = React.useMemo(
    () => ({ dry_run: 'true', tenant_id: tenantId || undefined, limit: 100, offset: 0 }),
    [tenantId]
  );

  const manualPreviewQuery = useRuntimeQuery({
    queryKey: [...runtimeQueryKeys.retryPreview(previewParams), normalizedExecutionId],
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const response = await runtimeDiagnosticsService.getRetryPreview(previewParams);
      return response.data;
    },
  });

  const planPreviewQuery = useRuntimeQuery({
    queryKey: [...runtimeQueryKeys.retryPlanPreview(previewParams), normalizedExecutionId],
    enabled: Boolean(tenantId),
    queryFn: async () => {
      const response = await runtimeRetryService.getPlanPreview(previewParams);
      return response.data;
    },
  });

  const executionMatch = React.useMemo(() => buildExecutionMatch(execution), [execution]);

  const manualItemsAll = Array.isArray(manualPreviewQuery.data?.items) ? manualPreviewQuery.data.items : [];
  const planItemsAll = Array.isArray(planPreviewQuery.data?.items) ? planPreviewQuery.data.items : [];
  const canMatchByIds = hasMatchSignals(executionMatch);

  const manualItems = React.useMemo(
    () =>
      canMatchByIds
        ? manualItemsAll.filter((item) => includeByExecution(item, executionMatch))
        : manualItemsAll.slice(0, 20),
    [manualItemsAll, executionMatch, canMatchByIds]
  );

  const planItems = React.useMemo(
    () =>
      canMatchByIds
        ? planItemsAll.filter((item) => includeByExecution(item, executionMatch))
        : planItemsAll.slice(0, 20),
    [planItemsAll, executionMatch, canMatchByIds]
  );

  const simulatedOnly = !tenantId || (!manualPreviewQuery.data && !planPreviewQuery.data);

  const dryRunFlag = Boolean(manualPreviewQuery.data?.dry_run ?? planPreviewQuery.data?.dry_run ?? true);
  const providerTarget = String(execution?.provider_key || '').trim() || '-';

  const eligibility = {
    retryable: Number(planItems.filter((item) => item.retry_ready === true).length),
    blocked: Number(planItems.filter((item) => item.retry_ready !== true).length),
  };

  const warnings = React.useMemo(() => {
    const list = [];

    if (simulatedOnly) {
      list.push('Preview especifico por execution nao existe no backend; exibindo composicao visual read-only.');
    }

    if (!tenantId) {
      list.push('Execution sem tenant_id: endpoints de retry preview tenant-scoped nao puderam ser consultados.');
    }

    if (planItems.length === 0 && manualItems.length === 0) {
      list.push('Nenhum item de retry correlacionado encontrado para esta execution nos previews tenant-scoped.');
    }

    if (tenantId && !canMatchByIds) {
      list.push('Execution sem issue_id/invoice_id/charge_id suficientes; exibindo amostra tenant-scoped para analise manual.');
    }

    if (eligibility.blocked > 0) {
      list.push('Existem itens bloqueados para retry. Revise blockers e reason_codes antes de qualquer acao.');
    }

    return list;
  }, [simulatedOnly, tenantId, planItems.length, manualItems.length, eligibility.blocked, canMatchByIds]);

  const estimatedRisk = computeEstimatedRisk(manualItems, planItems);

  const operationalSummary = React.useMemo(() => {
    const totalAffected = Math.max(planItems.length, manualItems.length);
    return {
      totalAffected,
      retryable: eligibility.retryable,
      blocked: eligibility.blocked,
      previewMode: simulatedOnly ? 'simulado' : 'real_tenant_scoped',
    };
  }, [planItems.length, manualItems.length, eligibility.retryable, eligibility.blocked, simulatedOnly]);

  const manualColumns = [
    {
      title: 'Issue',
      dataIndex: 'issue_id',
      key: 'issue_id',
      render: (value) => value || '-',
    },
    {
      title: 'Retry Action',
      dataIndex: 'retry_action',
      key: 'retry_action',
      render: (value) => value || '-',
    },
    {
      title: 'Ready',
      dataIndex: 'retry_ready',
      key: 'retry_ready',
      render: (value) => (value ? <Tag color="green">SIM</Tag> : <Tag color="red">NAO</Tag>),
    },
    {
      title: 'Risk',
      dataIndex: 'risk_level',
      key: 'risk_level',
      render: (value) => riskTag(value),
    },
    {
      title: 'Preview Reason',
      dataIndex: 'preview_reason',
      key: 'preview_reason',
      render: (value) => value || '-',
    },
  ];

  const planColumns = [
    {
      title: 'Issue',
      dataIndex: 'issue_id',
      key: 'issue_id',
      render: (value) => value || '-',
    },
    {
      title: 'Retry Action',
      dataIndex: 'retry_action',
      key: 'retry_action',
      render: (value) => value || '-',
    },
    {
      title: 'Ready',
      dataIndex: 'retry_ready',
      key: 'retry_ready',
      render: (value) => (value ? <Tag color="green">SIM</Tag> : <Tag color="red">NAO</Tag>),
    },
    {
      title: 'Reason Codes',
      dataIndex: 'reason_codes',
      key: 'reason_codes',
      render: (value) =>
        Array.isArray(value) && value.length ? value.map((item) => <Tag key={String(item)}>{String(item)}</Tag>) : '-',
    },
    {
      title: 'Provider Snapshot',
      key: 'provider_snapshot',
      render: (_, row) => String(row?.status_snapshot?.provider_key || '-'),
    },
  ];

  const detailError = executionQuery.isError
    ? buildApiErrorMessage(executionQuery.error, 'Nao foi possivel carregar execution detail.')
    : null;

  if (!normalizedExecutionId) {
    return (
      <div style={{ padding: 24 }}>
        <RuntimeErrorState
          title="Execution ID invalido"
          description="Nao foi possivel identificar a execucao para preview."
          onRetry={() => navigate('/platform/saas-billing/runtime/history')}
          retryLabel="Voltar para Runtime History"
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/platform/saas-billing/runtime/history/executions/${encodeURIComponent(normalizedExecutionId)}`)}
            style={{ marginBottom: 12 }}
          >
            Voltar para Execution Detail
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            Runtime Retry Preview
          </Title>
          <Text type="secondary">Preview operacional read-only antes de qualquer tentativa real.</Text>
        </div>

        {executionQuery.isLoading ? <RuntimeLoadingState tip="Carregando execution base..." /> : null}

        {!executionQuery.isLoading && executionQuery.isError ? (
          <RuntimeErrorState
            title="Erro ao carregar execution"
            description={detailError?.message || 'Falha inesperada ao carregar execution detail.'}
            onRetry={() => executionQuery.refetch()}
          />
        ) : null}

        {!executionQuery.isLoading && !executionQuery.isError && !execution ? (
          <RuntimeEmptyState
            title="Execution nao encontrada"
            description="Nao ha dados para o execution_id informado."
          />
        ) : null}

        {!executionQuery.isLoading && !executionQuery.isError && execution ? (
          <>
            <Card>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Execution ID">{execution.execution_id || '-'}</Descriptions.Item>
                <Descriptions.Item label="Correlation ID">
                  <RuntimeCorrelationChip correlationId={execution.correlation_id} />
                </Descriptions.Item>
                <Descriptions.Item label="Status Atual">{statusBadge(execution.status)}</Descriptions.Item>
                <Descriptions.Item label="Tenant ID">{execution.tenant_id || '-'}</Descriptions.Item>
                <Descriptions.Item label="Provider Alvo">{providerTarget}</Descriptions.Item>
                <Descriptions.Item label="Dry Run Flag">
                  {dryRunFlag ? <Tag color="blue">TRUE</Tag> : <Tag>FALSE</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Started At">{formatDateTime(execution.started_at)}</Descriptions.Item>
                <Descriptions.Item label="Finished At">{formatDateTime(execution.finished_at)}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic title="Itens afetados" value={operationalSummary.totalAffected} />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic title="Elegiveis para retry" value={operationalSummary.retryable} />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card>
                  <Statistic
                    title="Risco estimado"
                    value={String(estimatedRisk).toUpperCase()}
                    suffix={riskTag(estimatedRisk)}
                  />
                </Card>
              </Col>
            </Row>

            {warnings.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message="Warnings operacionais"
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {warnings.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                }
              />
            ) : null}

            <Card title="Resumo operacional">
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Modo de preview">
                  {operationalSummary.previewMode === 'simulado' ? 'Simulado (visual)' : 'Real (tenant-scoped, read-only)'}
                </Descriptions.Item>
                <Descriptions.Item label="Fonte de dados">
                  execution detail + retry preview endpoints read-only
                </Descriptions.Item>
                <Descriptions.Item label="Limitacao principal">
                  Endpoints de preview nao sao execution-scoped; correlacao por issue_id/invoice_id/charge_id.
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Preview itens afetados (manual retry preview)">
              {tenantId && manualPreviewQuery.isLoading ? (
                <RuntimeLoadingState tip="Carregando manual retry preview..." />
              ) : null}

              {tenantId && !manualPreviewQuery.isLoading && manualPreviewQuery.isError ? (
                <RuntimeErrorState
                  title="Erro no manual retry preview"
                  description={
                    buildApiErrorMessage(
                      manualPreviewQuery.error,
                      'Nao foi possivel carregar manual retry preview.'
                    ).message
                  }
                  onRetry={() => manualPreviewQuery.refetch()}
                />
              ) : null}

              {!tenantId ? (
                <RuntimeEmptyState
                  title="Preview real indisponivel"
                  description="Execution sem tenant_id; exibindo apenas preview visual/simulado."
                />
              ) : null}

              {tenantId && !manualPreviewQuery.isLoading && !manualPreviewQuery.isError && manualItems.length === 0 ? (
                <RuntimeEmptyState
                  title="Sem itens correlacionados"
                  description="Nao houve match de itens do preview com a execution selecionada."
                />
              ) : null}

              {tenantId && !manualPreviewQuery.isLoading && !manualPreviewQuery.isError && manualItems.length > 0 ? (
                <Table
                  rowKey={(record) =>
                    [record?.issue_id, record?.invoice_id, record?.charge_id, record?.tenant_id]
                      .map((item) => String(item || 'na'))
                      .join('|')
                  }
                  columns={manualColumns}
                  dataSource={manualItems}
                  pagination={{ pageSize: 8, hideOnSinglePage: true }}
                />
              ) : null}
            </Card>

            <Card title="Retry plan preview (dry-run read-only)">
              {tenantId && planPreviewQuery.isLoading ? (
                <RuntimeLoadingState tip="Carregando retry plan preview..." />
              ) : null}

              {tenantId && !planPreviewQuery.isLoading && planPreviewQuery.isError ? (
                <RuntimeErrorState
                  title="Erro no retry plan preview"
                  description={
                    buildApiErrorMessage(
                      planPreviewQuery.error,
                      'Nao foi possivel carregar retry plan preview.'
                    ).message
                  }
                  onRetry={() => planPreviewQuery.refetch()}
                />
              ) : null}

              {tenantId && !planPreviewQuery.isLoading && !planPreviewQuery.isError && planItems.length === 0 ? (
                <RuntimeEmptyState
                  title="Sem itens no plano correlacionado"
                  description="Nenhum item do plano de retry correspondeu a esta execution."
                />
              ) : null}

              {tenantId && !planPreviewQuery.isLoading && !planPreviewQuery.isError && planItems.length > 0 ? (
                <Table
                  rowKey={(record) =>
                    [record?.issue_id, record?.invoice_id, record?.charge_id, record?.tenant_id]
                      .map((item) => String(item || 'na'))
                      .join('|')
                  }
                  columns={planColumns}
                  dataSource={planItems}
                  pagination={{ pageSize: 8, hideOnSinglePage: true }}
                />
              ) : null}
            </Card>
          </>
        ) : null}
      </Space>
    </div>
  );
};

export default PlatformRuntimeRetryPreview;
